import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Grid,
  TextField,
  Typography,
  alpha,
} from "@mui/material";
import { Formik, Form } from "formik";
import {
  MiniAccount,
  VerifyAndResetPasswordModal,
} from "../../allComponents/AllComponents";
import { useDispatch, useSelector } from "react-redux";
import { selectCurrentUser } from "../../../redux/selectors/userSelector";
import { colors } from "../../../utility/types/constant";
import UserProfileHeader from "../../userprofile-header/UserProfileHeader";
import { useEffect, useState } from "react";
import DrawerNavbar from "../../drawerNavBar/DrawerNavbar";
import { IPasswordResetPayload } from "../../../utility/types/userType";
import { AppDispatch } from "../../../redux/store/store";
import { passWordReset } from "../../../redux/actions/userActions";
import { passwordResetValidation } from "../../../utility/validation/passwordResetValidation";
import LockResetIcon from "@mui/icons-material/LockReset";

const PasswordResetModal = () => {
  const dispatch = useDispatch<AppDispatch>();

  const [openNav, setOpenNav] = useState(false);
  const [showVerifyModal, setShowVerifyModal] = useState(false);
  const [successAlert, setSuccessAlert] = useState(false);
  const [errorAlert, setErrorAlert] = useState("");
  const [isLoading, setIsLoading] = useState(false);

  const userData = useSelector(selectCurrentUser);

  const initialValues = { email: "" };

  const handleCloseSuccessAlert = () => setSuccessAlert(false);
  const handleCloseErrorAlert = () => setErrorAlert("");

  const handleSubmit = async (values: IPasswordResetPayload) => {
    setIsLoading(true);
    try {
      await dispatch(passWordReset({ email: values.email })).unwrap();
      setSuccessAlert(true);
      setTimeout(() => {
        setShowVerifyModal(true);
      }, 5000);
    } catch (err: any) {
      setSuccessAlert(false);
      const message =
        (typeof err === "string" && err) ||
        err?.message ||
        err?.error ||
        "We couldn't find an account with that email address. Please check and try again.";
      setErrorAlert(message);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    let timeOutId: any;
    return () => {
      if (timeOutId) clearTimeout(timeOutId);
    };
  });

  return (
    <>
      <UserProfileHeader onOpenNav={() => setOpenNav(true)} />

      <Box
        component="main"
        sx={{
          flexGrow: 1,
          py: { xs: 4, md: 8 },
          px: { xs: 2, sm: 3 },
          mt: 7,
        }}
      >
        <DrawerNavbar openNav={openNav} onCloseNav={() => setOpenNav(false)} />

        <Box sx={{ maxWidth: 1100, mx: "auto" }}>
          {/* Page heading */}
          {/* <Typography
            sx={{
              fontFamily: "'Montserrat', sans-serif",
              fontWeight: 800,
              color: colors.primary,
              fontSize: { xs: "1.5rem", md: "1.9rem" },
              mb: 4,
            }}
          >
            Your Account
          </Typography> */}

          <Grid container spacing={4} alignItems="flex-start">
            {/* Mini account card */}
            <Grid item xs={12} md={4} lg={3}>
              <MiniAccount
                name={userData?.firstName ?? ""}
                image={userData?.profileImage ?? ""}
                email={userData?.email ?? ""}
              />
            </Grid>

            {/* Password reset card */}
            <Grid item xs={12} md={8} lg={9}>
              <Box
                sx={{
                  bgcolor: "#fff",
                  borderRadius: 3,
                  boxShadow: "0 8px 40px rgba(4, 13, 69, 0.10)",
                  overflow: "hidden",
                }}
              >
                {/* Card header band */}
                <Box
                  sx={{
                    bgcolor: colors.primary,
                    px: 4,
                    py: 3,
                    display: "flex",
                    alignItems: "center",
                    gap: 2,
                  }}
                >
                  <Box
                    sx={{
                      width: 40,
                      height: 40,
                      borderRadius: "50%",
                      bgcolor: alpha(colors.mainSecondary, 0.18),
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      flexShrink: 0,
                    }}
                  >
                    <LockResetIcon sx={{ color: colors.mainSecondary, fontSize: "1.3rem" }} />
                  </Box>
                  <Box>
                    <Typography
                      sx={{
                        fontFamily: "'Montserrat', sans-serif",
                        fontWeight: 800,
                        color: "#fff",
                        fontSize: "1.05rem",
                        lineHeight: 1.2,
                      }}
                    >
                      Password Reset
                    </Typography>
                    <Typography
                      variant="caption"
                      sx={{ color: alpha("#fff", 0.55) }}
                    >
                      Enter your email to receive a verification code
                    </Typography>
                  </Box>
                </Box>

                {/* Card body */}
                <Box sx={{ px: { xs: 3, sm: 4 }, py: 4 }}>
                  {successAlert && (
                    <Alert
                      severity="success"
                      onClose={handleCloseSuccessAlert}
                      sx={{ mb: 3, borderRadius: 2 }}
                    >
                      Check your inbox — a verification code is on its way.
                    </Alert>
                  )}

                  {errorAlert && (
                    <Alert
                      severity="error"
                      onClose={handleCloseErrorAlert}
                      sx={{ mb: 3, borderRadius: 2 }}
                    >
                      {errorAlert}
                    </Alert>
                  )}

                  <Formik
                    initialValues={initialValues}
                    onSubmit={handleSubmit}
                    validationSchema={passwordResetValidation}
                  >
                    {({ values, handleChange, errors, touched }) => (
                      <Form>
                        <Grid container spacing={3} alignItems="flex-end">
                          <Grid item xs={12} sm={7} md={6}>
                            <TextField
                              size="small"
                              fullWidth
                              label="Email Address"
                              name="email"
                              autoComplete="email"
                              onChange={handleChange}
                              value={values.email}
                              error={!!(errors?.email && touched?.email)}
                              helperText={errors.email && touched.email && errors.email}
                            />
                          </Grid>

                          <Grid item xs={12} sm={5} md={4}>
                            <Button
                              disabled={isLoading}
                              type="submit"
                              fullWidth
                              variant="contained"
                              sx={{
                                bgcolor: colors.mainSecondary,
                                color: colors.primary,
                                fontWeight: 800,
                                py: 1.05,
                                borderRadius: 2,
                                textTransform: "none",
                                fontSize: "0.92rem",
                                letterSpacing: 0.3,
                                boxShadow: `0 4px 14px ${alpha(colors.mainSecondary, 0.35)}`,
                                "&:hover": {
                                  bgcolor: colors.mainSecondary,
                                  boxShadow: `0 6px 20px ${alpha(colors.mainSecondary, 0.5)}`,
                                },
                              }}
                            >
                              {isLoading ? (
                                <CircularProgress size={20} sx={{ color: colors.primary }} />
                              ) : (
                                "Send Reset Code"
                              )}
                            </Button>
                          </Grid>
                        </Grid>
                      </Form>
                    )}
                  </Formik>
                </Box>
              </Box>
            </Grid>
          </Grid>
        </Box>
      </Box>

      {showVerifyModal && (
        <VerifyAndResetPasswordModal
          open={showVerifyModal}
          handleClose={() => setShowVerifyModal(false)}
        />
      )}
    </>
  );
};

export default PasswordResetModal;
